import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { RefreshCw, Plus, Search, Receipt, Trash2 } from "lucide-react";
import { api, errorToMessage } from "@/lib/apiClient";
import { canSalesWrite, useAuth } from "@/lib/auth";
import { SALES } from "@/constants/testIds";

const STATUS_COLORS = {
  DRAFT: "bg-slate-100 text-slate-700",
  CONFIRMED: "bg-sky-100 text-sky-800",
  PACKED: "bg-indigo-100 text-indigo-800",
  DISPATCHED: "bg-violet-100 text-violet-800",
  COMPLETED: "bg-emerald-100 text-emerald-800",
  CANCELLED: "bg-rose-100 text-rose-800",
};
const PAY_COLORS = {
  UNPAID: "bg-rose-100 text-rose-800",
  PARTIAL: "bg-amber-100 text-amber-800",
  PAID: "bg-emerald-100 text-emerald-800",
};

function Chip({ text, cls }) {
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${cls || "bg-slate-100 text-slate-700"}`}>
      {text}
    </span>
  );
}

export default function SalesPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ total: 0, total_pages: 1 });
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const params = useMemo(() => ({
    page, page_size: pageSize,
    q: q || undefined,
    status: status || undefined,
  }), [q, status, page, pageSize]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get("/sales", { params });
      setRows(data.items);
      setMeta(data.meta);
    } catch (e) {
      setError(errorToMessage(e, "Failed to load sales orders"));
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => { load(); }, [load]);

  const canDelete = ["SUPER_ADMIN", "ADMIN"].includes(user?.role);
  const deleteRow = async (e, r) => {
    e.stopPropagation();
    const msg = `Permanently delete this invoice?\n\nInvoice No: ${r.invoice_no || "—"} (SO ${r.so_no})\nCustomer: ${r.customer_name}\nAmount: ₹${r.grand_total}\n\nThis cannot be undone.`;
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/sales/${r.id}`);
      await load();
    } catch (err) {
      setError(errorToMessage(err, "Delete failed"));
    }
  };

  return (
    <div className="space-y-6" data-testid="sales-page">
      <div className="flex items-baseline justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Sales Orders</h1>
          <p className="text-sm text-slate-500 mt-1">
            Retail, walk-in and corporate orders. Confirm to reserve inventory; dispatch after packing.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={load}
            className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50">
            <RefreshCw size={14} /> Refresh
          </button>
          {canSalesWrite(user) && (
            <button type="button" data-testid={SALES.newBtn}
              onClick={() => navigate("/admin/sales/new")}
              className="inline-flex items-center gap-2 rounded-md bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800">
              <Plus size={14} /> New Sale
            </button>
          )}
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white p-4 grid grid-cols-1 sm:grid-cols-4 gap-3 shadow-sm">
        <div className="sm:col-span-2 relative">
          <Search size={14} className="absolute left-3 top-2.5 text-slate-400" />
          <input type="text" value={q}
            onChange={(e) => { setQ(e.target.value); setPage(1); }}
            data-testid={SALES.searchInput}
            placeholder="SO no · customer · mobile · invoice · barcode · serial"
            className="w-full rounded-md border border-slate-200 pl-9 pr-3 py-2 text-sm outline-none focus:ring-2 focus:ring-slate-800"
          />
        </div>
        <select value={status}
          onChange={(e) => { setStatus(e.target.value); setPage(1); }}
          data-testid={SALES.statusFilter}
          className="rounded-md border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-slate-800">
          <option value="">All statuses</option>
          {["DRAFT","CONFIRMED","PACKED","DISPATCHED","COMPLETED","CANCELLED"].map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </div>

      {error && (
        <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {error}
        </div>
      )}

      <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
        <table className="w-full text-sm" data-testid={SALES.listTable}>
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="text-left px-4 py-3">SO No.</th>
              <th className="text-left px-4 py-3">Date</th>
              <th className="text-left px-4 py-3">Customer</th>
              <th className="text-left px-4 py-3">Items</th>
              <th className="text-right px-4 py-3">Grand Total</th>
              <th className="text-right px-4 py-3">Balance</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Payment</th>
              <th className="text-left px-4 py-3">Invoice</th>
              <th className="px-2 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={10} className="px-4 py-10 text-center text-slate-400">Loading…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={10} className="px-4 py-10 text-center text-slate-400">No sales orders.</td></tr>
            )}
            {!loading && rows.map((r) => (
              <tr key={r.id} data-testid={SALES.listRow(r.id)}
                className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                onClick={() => navigate(`/admin/sales/${r.id}`)}>
                <td className="px-4 py-3 font-mono text-xs text-slate-900">{r.so_no}</td>
                <td className="px-4 py-3 text-xs text-slate-600">{r.order_date}</td>
                <td className="px-4 py-3">
                  <div className="text-slate-900 font-medium">{r.customer_name}</div>
                  <div className="text-xs text-slate-500">
                    {r.customer_mobile}{r.company_name ? ` · ${r.company_name}` : ""}
                  </div>
                </td>
                <td className="px-4 py-3 text-xs text-slate-600">{r.lines_count}</td>
                <td className="px-4 py-3 text-right font-mono text-xs text-slate-900">₹{r.grand_total}</td>
                <td className="px-4 py-3 text-right font-mono text-xs text-slate-700">₹{r.balance}</td>
                <td className="px-4 py-3"><Chip text={r.status} cls={STATUS_COLORS[r.status]} /></td>
                <td className="px-4 py-3"><Chip text={r.payment_status} cls={PAY_COLORS[r.payment_status]} /></td>
                <td className="px-4 py-3 text-xs text-slate-600">
                  {r.invoice_no ? (
                    <span className="inline-flex items-center gap-1 font-mono">
                      <Receipt size={12} /> {r.invoice_no}
                    </span>
                  ) : "—"}
                </td>
                <td className="px-2 py-3 text-right">
                  {canDelete && ["DRAFT", "CONFIRMED", "CANCELLED"].includes(r.status) && Number(r.paid_total || 0) === 0 && (
                    <button type="button" title="Delete invoice" data-testid={`sales-delete-${r.id}`}
                      onClick={(e) => deleteRow(e, r)}
                      className="rounded-md p-1.5 text-rose-600 hover:bg-rose-50">
                      <Trash2 size={14} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="flex items-center justify-between px-4 py-3 text-xs text-slate-500 border-t border-slate-100">
          <span>{meta.total} record{meta.total === 1 ? "" : "s"}</span>
          <div className="flex items-center gap-2">
            <button data-testid={SALES.pagePrev} disabled={page <= 1}
              onClick={() => setPage(page - 1)}
              className="rounded-md border border-slate-300 bg-white px-2 py-1 disabled:opacity-40">Prev</button>
            <span>Page {page} of {meta.total_pages || 1}</span>
            <button data-testid={SALES.pageNext} disabled={page >= (meta.total_pages || 1)}
              onClick={() => setPage(page + 1)}
              className="rounded-md border border-slate-300 bg-white px-2 py-1 disabled:opacity-40">Next</button>
          </div>
        </div>
      </div>
    </div>
  );
}
